import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { CreditCard, StickyNote, MapPin, Users, Clock, UtensilsCrossed, Banknote, Bike, CheckCircle, XCircle, ChefHat, AlertCircle, Star } from "lucide-react";
import Header from "./components/Header";
import OrderReviewSection from "./components/OrderReviewSection";
import PrimaryButton from "./components/PrimaryButton";
import BackButton from "./components/BackButton";
import appConfig from "./config";
import { useAuthFetch } from "./hooks/useAuthFetch";
import useNav from "./hooks/useNav";
import { useRestaurant } from "./context/RestaurantContext";
import { useCart } from "./context/CartContext";

const ORDER_STATUS_NEW              = 0;
const ORDER_STATUS_APPROVED         = 1;
const ORDER_STATUS_AWAITING_PAYMENT = 6;

const statusStyle = (status) => {
    if (status === 4) return "bg-green-100 text-green-700";
    if (status === 3) return "bg-blue-100 text-blue-700";
    if (status === 5) return "bg-red-100 text-red-600";
    if (status === ORDER_STATUS_AWAITING_PAYMENT) return "bg-orange-50 text-orange-500";
    return "bg-gray-100 text-gray-600";
};

const statusConfig = (status, isDelivery, info, cookingMinutes) => {
    const scheduledTime = !info?.asap && info?.time ? info.time : null;
    const cookingHint = cookingMinutes ? `Примерное время приготовления — ${cookingMinutes} мин` : null;

    switch (status) {
        case 0: return {
            icon: Clock, iconBg: "bg-gray-100", iconColor: "text-gray-400", pulse: true,
            title: "Ожидает подтверждения",
            description: "Заказ отправлен в ресторан, ожидаем подтверждения",
            hint: "Вы получите уведомление в Telegram — пока можно закрыть эту страницу",
        };
        case 1: return {
            icon: CheckCircle, iconBg: "bg-green-50", iconColor: "text-green-500",
            title: "Заказ принят",
            description: scheduledTime
                ? `Ресторан принял ваш заказ — начнут готовить к ${scheduledTime}`
                : "Ресторан принял ваш заказ и скоро приступит к приготовлению",
            hint: isDelivery
                ? "Ожидайте — курьер свяжется с вами после того как заказ будет готов"
                : "Ожидайте — мы уведомим вас когда заказ будет готов",
        };
        case 2: return {
            icon: CheckCircle, iconBg: "bg-green-50", iconColor: "text-green-500",
            title: "Заказ принят",
            description: scheduledTime
                ? `Оплата получена — ресторан начнёт готовить к ${scheduledTime}`
                : "Оплата получена, ресторан скоро приступит к приготовлению",
            hint: isDelivery
                ? "Ожидайте — курьер свяжется с вами после того как заказ будет готов"
                : "Ожидайте — мы уведомим вас когда заказ будет готов",
        };
        case 3: return {
            icon: ChefHat, iconBg: "bg-brandBlue/10", iconColor: "text-brandBlue", pulse: true,
            title: "Готовится",
            description: cookingHint
                ? `Повара уже работают над вашим заказом · ${cookingHint}`
                : "Повара уже работают над вашим заказом",
            hint: isDelivery
                ? "Курьер заберёт заказ как только он будет готов и свяжется с вами"
                : "Подходите к стойке — заказ скоро будет готов",
        };
        case 4: return {
            icon: CheckCircle, iconBg: "bg-green-50", iconColor: "text-green-500",
            title: "Готово!",
            description: isDelivery ? "Заказ передан курьеру" : "Ваш заказ готов и ждёт вас",
            hint: isDelivery
                ? "Курьер уже в пути — ожидайте доставку"
                : "Подойдите к стойке и назовите номер заказа — #{id}",
        };
        case 5: return {
            icon: XCircle, iconBg: "bg-red-50", iconColor: "text-red-400",
            title: "Заказ отменён",
            description: "К сожалению, этот заказ был отменён",
            hint: "Свяжитесь с рестораном если считаете это ошибкой",
        };
        case 6: return {
            icon: AlertCircle, iconBg: "bg-orange-50", iconColor: "text-orange-400",
            title: "Ожидает оплаты",
            description: "Заказ будет передан в ресторан после оплаты",
            hint: "Нажмите кнопку «Оплатить заказ» ниже",
        };
        default: return {
            icon: Clock, iconBg: "bg-gray-100", iconColor: "text-gray-400",
            title: "Статус неизвестен",
            description: "",
            hint: "",
        };
    }
};

const paymentLabel = (method) => {
    if (method === "cash") return "Наличными";
    if (method === "online" || method === "card") return "Онлайн";
    return method || "—";
};

const OrderViewPage = () => {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [reviewDirty, setReviewDirty] = useState(false);
    const [leaveModal, setLeaveModal] = useState(false);
    const pendingActionRef = useRef(null);
    const reviewRef = useRef(null);
    const { authFetch } = useAuthFetch();
    const navigate = useNav();
    const { restaurant } = useRestaurant();
    const { clearCart, addItem, increaseQuantity } = useCart();

    useEffect(() => {
        if (!orderId) return;
        authFetch(`${appConfig.endpoint}/orders/${orderId}/view`)
            .then(data => setOrder(data?.order || null))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [orderId]);

    const doRepeat = () => {
        if (!order?.dishes?.length) return;
        clearCart();
        for (const dish of order.dishes) {
            addItem({ id: dish.id, title: dish.title, image: dish.image, price: dish.price });
            for (let i = 1; i < dish.quantity; i++) {
                increaseQuantity(dish.id);
            }
        }
        navigate("/order");
    };

    const guardedAction = (action, fn) => {
        if (reviewDirty) {
            pendingActionRef.current = fn;
            setLeaveModal(true);
        } else {
            fn();
        }
    };

    const handleRepeat  = () => guardedAction('repeat', doRepeat);
    const handleBack    = () => guardedAction('back', () => window.history.back());

    const handleLeaveConfirm = () => {
        setLeaveModal(false);
        pendingActionRef.current?.();
        pendingActionRef.current = null;
    };

    const handleLeaveCancel = () => {
        setLeaveModal(false);
        pendingActionRef.current = null;
    };

    const handleCancel = async () => {
        if (cancelling) return;
        setCancelling(true);
        try {
            await authFetch(`${appConfig.endpoint}/orders/${orderId}/cancel`, { method: 'POST' });
            setOrder(prev => ({ ...prev, status: 5, statusLabel: 'Отменён' }));
        } catch {
            // silent
        } finally {
            setCancelling(false);
        }
    };

    const scrollToReview = () =>
        reviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brandBlue" />
            </div>
        );
    }

    if (!order) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header />
                <div className="flex items-center gap-3 px-4 py-4 bg-white border-b border-gray-100">
                    <BackButton onClick={() => window.history.back()} />
                    <h1 className="text-lg font-semibold text-gray-800">Детали заказа</h1>
                </div>
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                    Заказ не найден
                </div>
            </div>
        );
    }

    const isDelivery = !!order.delivery;
    const paymentMethod = order.info?.payment_method;
    const countPerson = order.info?.countPerson;
    const cookingMinutes = order.dishes?.length
        ? Math.max(...order.dishes.map(d => d.cooking_time || 0).filter(t => t > 0), 0) || null
        : null;

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <div className="flex items-center gap-3 px-4 py-4 bg-white border-b border-gray-100 sticky top-0 z-10">
                <BackButton onClick={handleBack} />
                <div className="flex-1 min-w-0">
                    <h1 className="text-lg font-semibold text-gray-800">Заказ #{order.id}</h1>
                    {order.createdAt && (
                        <p className="text-xs text-gray-400 mt-0.5">{order.createdAt}</p>
                    )}
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${statusStyle(order.status)}`}>
                    {order.statusLabel}
                </span>
            </div>

            <div className="max-w-screen-sm mx-auto px-4 pt-4 pb-28 space-y-4">

                {/* Status */}
                {(() => {
                    const cfg = statusConfig(order.status, isDelivery, order.info, cookingMinutes);
                    const hint = cfg.hint?.replace("#{id}", `#${order.id}`);
                    const Icon = cfg.icon;
                    return (
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full ${cfg.iconBg} flex items-center justify-center shrink-0`}>
                                    <Icon size={24} className={`${cfg.iconColor}${cfg.pulse ? " animate-pulse" : ""}`} strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-base font-bold text-gray-900">{cfg.title}</p>
                                    {cfg.description && (
                                        <p className="text-sm text-gray-500 mt-0.5">{cfg.description}</p>
                                    )}
                                </div>
                            </div>
                            {hint && (
                                <div className="mt-4 pt-3 border-t border-gray-50">
                                    <p className="text-xs text-gray-400">{hint}</p>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Summary */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    {order.restaurant && (
                        <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">{order.restaurant}</p>
                    )}

                    {/* Сумма + оплата */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-brandBlue/5 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500 mb-1">Сумма</p>
                            <p className="text-xl font-bold text-brandBlue">{order.amount} ₽</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500 mb-1">Оплата</p>
                            <p className="text-sm font-semibold text-gray-800">{paymentLabel(paymentMethod)}</p>
                            {paymentMethod === "cash" && (
                                <p className="text-xs text-gray-400 mt-0.5">При получении</p>
                            )}
                        </div>
                    </div>

                    {/* Детали */}
                    <div className="space-y-2.5">
                        {/* Тип заказа */}
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                {isDelivery
                                    ? <Bike size={15} className="text-gray-400" />
                                    : <UtensilsCrossed size={15} className="text-gray-400" />
                                }
                            </div>
                            <p className="text-sm text-gray-600">
                                {isDelivery ? "Доставка" : "В ресторане"}
                                {isDelivery && order.delivery?.address ? ` · ${order.delivery.address}` : ""}
                            </p>
                        </div>

                        {/* Время */}
                        {order.info && (
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                    <Clock size={15} className="text-gray-400" />
                                </div>
                                <p className="text-sm text-gray-600">
                                    {order.info.asap ? "Как можно скорее" : "К определённому времени"}
                                    {order.info.time ? ` · ${order.info.time}` : ""}
                                </p>
                            </div>
                        )}

                        {/* Персоны (только не доставка) */}
                        {!isDelivery && countPerson >= 1 && (
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                    <Users size={15} className="text-gray-400" />
                                </div>
                                <p className="text-sm text-gray-600">
                                    {countPerson} {countPerson === 1 ? "персона" : countPerson < 5 ? "персоны" : "персон"}
                                </p>
                            </div>
                        )}

                        {/* Столик */}
                        {order.info?.table_name && (
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                    <MapPin size={15} className="text-gray-400" />
                                </div>
                                <p className="text-sm text-gray-600">{order.info.table_name}</p>
                            </div>
                        )}
                    </div>

                    {/* New: waiting for restaurant confirmation — client can cancel */}
                    {order.status === ORDER_STATUS_NEW && (
                        <button
                            onClick={handleCancel}
                            disabled={cancelling}
                            className="mt-4 w-full flex items-center justify-center gap-2 bg-red-50 text-red-500 py-3 px-4 rounded-xl font-medium text-sm active:scale-95 transition-all disabled:opacity-50"
                        >
                            <XCircle size={18} />
                            {cancelling ? "Отмена..." : "Отменить заказ"}
                        </button>
                    )}

                    {/* Approved + online: admin confirmed, client needs to choose payment method */}
                    {order.status === ORDER_STATUS_APPROVED && order.info?.payment_method === 'online' && (
                        <button
                            onClick={() => navigate('/payment', {
                                state: { order: { id: order.id, amount: order.amount, restaurantID: order.restaurantID } },
                            })}
                            className="mt-4 w-full flex items-center justify-center gap-2 bg-brandBlue text-white py-3 px-4 rounded-xl font-medium text-sm active:scale-95 transition-all"
                        >
                            <CreditCard size={18} />
                            Перейти к оплате
                        </button>
                    )}

                    {/* Awaiting payment: already picked a method, returning to finish */}
                    {order.status === ORDER_STATUS_AWAITING_PAYMENT && (
                        <div className="mt-4 flex flex-col gap-2">
                            <button
                                onClick={() => navigate(`/payment-processing?slug=${restaurant?.slug || ""}`, {
                                    state: {
                                        order: { id: order.id, amount: order.amount, restaurantID: order.restaurantID },
                                        paymentMethod: "card",
                                        restaurantId: order.restaurantID,
                                    },
                                })}
                                className="w-full flex items-center justify-center gap-2 bg-brandBlue text-white py-3 px-4 rounded-xl font-medium text-sm active:scale-95 transition-all"
                            >
                                <CreditCard size={18} />
                                Оплатить заказ
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={cancelling}
                                className="w-full flex items-center justify-center gap-2 bg-red-50 text-red-500 py-3 px-4 rounded-xl font-medium text-sm active:scale-95 transition-all disabled:opacity-50"
                            >
                                <XCircle size={18} />
                                {cancelling ? "Отмена..." : "Отменить заказ"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Dishes */}
                {order.dishes?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <UtensilsCrossed size={15} className="text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-700">Состав заказа</h2>
                        </div>
                        <div className="space-y-3">
                            {order.dishes.map((dish) => (
                                <div key={dish.id} className="flex items-center gap-3 border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                                    {dish.image ? (
                                        <img
                                            src={dish.image}
                                            alt={dish.title}
                                            className="w-14 h-14 rounded-xl object-cover shrink-0"
                                        />
                                    ) : (
                                        <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                                            <UtensilsCrossed size={20} className="text-gray-300" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">{dish.title}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">× {dish.quantity}</p>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-800 shrink-0">{dish.price} ₽</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Note */}
                {order.note && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <div className="flex items-center gap-2 mb-2">
                            <StickyNote size={15} className="text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-700">Комментарий</h2>
                        </div>
                        <p className="text-sm text-gray-600">{order.note}</p>
                    </div>
                )}

                {/* Review — only for completed orders */}
                {order.status === 4 && (
                    <div ref={reviewRef}>
                        <OrderReviewSection
                            orderId={order.id}
                            dishes={order.dishes || []}
                            onDirtyChange={setReviewDirty}
                        />
                    </div>
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 shadow-lg">
                <div className="max-w-screen-sm mx-auto flex gap-3">
                    {order.status === 4 && (
                        <button
                            onClick={scrollToReview}
                            className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 active:scale-95 transition-all shrink-0"
                        >
                            <Star size={15} className="text-amber-400 fill-amber-400" />
                            Отзыв
                        </button>
                    )}
                    <PrimaryButton onClick={handleRepeat} className="flex-1 py-3">
                        Повторить заказ
                    </PrimaryButton>
                </div>
            </div>

            {leaveModal && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-8">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
                        <p className="text-base font-semibold text-gray-800 mb-1">Уйти без сохранения?</p>
                        <p className="text-sm text-gray-400 mb-5">Вы написали отзыв, но не сохранили его. Если уйдёте — он будет потерян.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleLeaveCancel}
                                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 active:scale-95 transition-all"
                            >
                                Остаться
                            </button>
                            <button
                                onClick={handleLeaveConfirm}
                                className="flex-1 py-3 rounded-xl bg-red-50 text-sm font-medium text-red-500 active:scale-95 transition-all"
                            >
                                Уйти
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderViewPage;